const API_PREFIX = "/api/v2";
const BUSINESS_PREFIX = `${API_PREFIX}/business`;

const hasBusinessContext = (
  businessId?: string | null
): businessId is string => {
  return typeof businessId === "string" && businessId.trim().length > 0;
};

export function resolveBusinessRoute(
  businessId: string | null | undefined,
  personalPath: string
): string {
  if (!hasBusinessContext(businessId)) {
    return personalPath;
  }

  if (personalPath.startsWith(BUSINESS_PREFIX)) {
    return personalPath;
  }

  if (personalPath.startsWith(`${API_PREFIX}/`)) {
    const suffix = personalPath.slice(API_PREFIX.length);
    return `${BUSINESS_PREFIX}${suffix}`;
  }

  return personalPath;
}

export function stripBusinessId<T extends Record<string, unknown>>(
  params: T
): Omit<T, "businessId"> {
  if (!params || !("businessId" in params)) {
    return params;
  }
  const { businessId: _ignored, ...rest } = params;
  return rest as Omit<T, "businessId">;
}

export function withBusinessContext<T extends Record<string, unknown>>(
  businessId: string | null | undefined,
  params?: T
): T & { businessId?: string } {
  const base = (params ?? ({} as T)) as T & { businessId?: string };
  if (!hasBusinessContext(businessId)) {
    return base;
  }
  return { ...base, businessId: businessId.trim() };
}

export const isBusinessActive = (businessId?: string | null): boolean =>
  hasBusinessContext(businessId);

export function appendBusinessIdToUrl(
  url: string,
  businessId?: string | null
): string {
  if (!hasBusinessContext(businessId)) {
    return url;
  }
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}businessId=${encodeURIComponent(businessId.trim())}`;
}

export function resolveBusinessUrlWithQuery(
  businessId: string | null | undefined,
  personalPath: string
): string {
  return appendBusinessIdToUrl(
    resolveBusinessRoute(businessId, personalPath),
    businessId
  );
}
